/**
 * Checkliste Trinkwasser Status Constants
 *
 * The four phase statuses of the drinking water checklist workflow. The German labels
 * these keys map to are deliberately not unique across the app (another workflow also
 * has a "Vorabklärung"), so always match on the key, never on the displayed name.
 */

export const DW_CHECKLIST_STATUSES = {
    PRE_CLARIFICATION: 'dw_checklist_pre_clarification_sub_task_status',
    ORDER_SCHEDULING: 'dw_checklist_order_scheduling_sub_task_status',
    AWAITING_LAB_DATE: 'dw_checklist_awaiting_lab_date_sub_task_status',
    AWAITING_LAB_REPORT: 'dw_checklist_awaiting_lab_report_sub_task_status',
    CANCELED: 'canceled_sub_task_status',
    RESOLVED: 'resolved_sub_task_status',
} as const;

export type DwChecklistStatus = typeof DW_CHECKLIST_STATUSES[keyof typeof DW_CHECKLIST_STATUSES];

/**
 * The phase statuses in workflow order, used to decide whether a phase is already done.
 */
export const DW_CHECKLIST_PHASE_ORDER: DwChecklistStatus[] = [
    DW_CHECKLIST_STATUSES.PRE_CLARIFICATION,
    DW_CHECKLIST_STATUSES.ORDER_SCHEDULING,
    DW_CHECKLIST_STATUSES.AWAITING_LAB_DATE,
    DW_CHECKLIST_STATUSES.AWAITING_LAB_REPORT,
];

/**
 * What the user can choose at the end of phase 3 ("Labortermin abwarten").
 *
 * "Fortführen" continues into phase 4. Either of the other two closes this subtask and
 * opens a fresh checklist from phase 1 - the backend does that, driven by this value.
 */
export const DW_CHECKLIST_NEXT_ACTIONS: { id: string; name: string }[] = [
    { id: 'continue', name: 'Fortführen' },
    { id: 'reschedule', name: 'Neuterminierung' },
    { id: 'restart', name: 'Neubeginn' },
];

/**
 * The two choices that abandon the current checklist.
 */
export const DW_CHECKLIST_RESTART_ACTIONS = ['reschedule', 'restart'];

/**
 * The single answer a checkpoint parameter can take.
 *
 * The spec words every parameter as "Ja (Yes) or nothing", so this is a two-valued
 * choice rather than a checkbox: SelectField prepends a blank option of its own, which
 * is the "nothing" half.
 */
export const DW_CHECKLIST_ANSWER_OPTIONS: { id: string; name: string }[] = [
    { id: "ja", name: "Ja" },
];

/** The value stored in the form state when a checkpoint is answered "Ja". */
export const DW_CHECKLIST_ANSWER_YES = "ja";
